/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-unused-vars */

interface IPromiseMemoization<TData, T extends Promise<any>> {
    cache( data: TData, value: T ): () => void;
    fromCache( data: TData ): T | null;
}

class MemorizePromise<TKey extends PropertyKey = PropertyKey, TData = unknown> {

    public constructor(
        private readonly _memoization: IPromiseMemoization<TKey, Promise<TData>>
    ) {}

    public send( key: TKey, data: Promise<TData> ): void {

        const free = this._memoization.cache( key, data );
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        ( async () => {

            try {

                await data;

            } catch ( _ ) {
            } finally {

                free();

            }

        })();

    }

    public sync( key: TKey ): Promise<TData> | null {

        return this._memoization.fromCache( key );

    }

}

const defaultMemoization = (): IPromiseMemoization<
    PropertyKey,
    Promise<any>
> => {

    const cache: Record<PropertyKey, Promise<any> | undefined> = {};
    return {
        cache( data, value ) {

            if ( !cache[data] ) {

                cache[data] = value;

            }
            return () => {

                delete cache[data];

            };

        },
        fromCache( data ) {

            return cache[data] ?? null;

        },
    };

};

function createMemorizePromiseBaseFactory<
    TKey extends PropertyKey,
    TFn extends ( this: any, ...args: any[] ) => Promise<any>
>(
    getter: TFn,
    format: <TArgs extends Parameters<TFn>>(
        args: TArgs
    ) => readonly [TKey, Parameters<TFn>]
): TFn {

    let limiter: MemorizePromise;
    return function ( this: any, ...args: Parameters<TFn> ): Promise<any> {

        if ( !limiter ) {

            limiter = new MemorizePromise( defaultMemoization());

        }
        const [key, latestArgs] = format( args );
        const atomicData = limiter.sync( key );
        if ( atomicData === null ) {

            const result = getter.apply( this, latestArgs );
            limiter.send( key, result );
            return result;

        }
        return atomicData;

    } as TFn;

}

let singletonFnKey: PropertyKey;
const SingletonFnFormatter = <T extends any[]>( args: T ): readonly [PropertyKey, T] => {

    if ( !singletonFnKey ) {

        singletonFnKey = Symbol( '' );

    }
    return [singletonFnKey, args] as const;

};
export const SynPromiseBaseFactory = createMemorizePromiseBaseFactory;
export const SingletonPromiseFactory = <
    TGetter extends ( ...args: any[] ) => Promise<any>,
>( getter: TGetter ): TGetter => SynPromiseBaseFactory( getter, SingletonFnFormatter );
